import { useMemo } from 'react'
import { getAllocationChecks } from '@/components/shared/utils'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'

/**
 * Filters the current order items list down to those that need budget
 * allocation or re-allocation on the dashboard.
 *
 * An order qualifies when ALL of the following are true:
 *  - It is not expired (runStatus !== 'COMPLETED')
 *  - It has at least one campaign (orders with 0 campaigns are excluded)
 *  - getAllocationChecks reports showAllocationWarning === true
 *
 * The check uses budgetMetrics fields from the order item:
 *  - effectiveBudget  → budget that has spent or can still spend
 *  - ineffectiveBudget → budget that can no longer spend (under-delivered)
 *  - unallocatedBudget → budget not yet allocated to any campaign
 */
export const useOrdersWithAllocationAlerts = (orderItems: OrderItem[]): OrderItem[] => {
  return useMemo(() => {
    return orderItems.filter(orderItem => {
      if (orderItem.runStatus === 'COMPLETED') return false
      if (orderItem.campaignGroups.length === 0) return false

      const { showAllocationWarning } = getAllocationChecks({
        effectiveBudget: orderItem.budgetMetrics.effective,
        ineffectiveBudget: orderItem.budgetMetrics.ineffective,
        unallocatedBudget: orderItem.budgetMetrics.unallocated,
      })

      return showAllocationWarning
    })
  }, [orderItems])
}
